/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { refresh } from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';
import { get as getUser } from 'js/lib/user';

import { ButtonGroup, Dialog, InlineNotification, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { useResendVerificationEmail } from 'bundles/enterprise-legacy-learner-home/queryHooks/useResendVerificationEmail';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/unified-home-common';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  subtitle: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  error: css`
    margin-top: var(--cds-spacing-200);
  `,
};

/**
 * Verify-email modal shown to an invited/allowlisted learner with an unverified email on org home.
 * Built on the CDS Dialog (color="darkBlue") so it keeps the navy-header look of the program-home
 * modal while staying on the design system — no legacy phoenix Modal chrome. Copy mirrors the
 * program-home auto-join modal; the dialog's built-in close (X) serves as dismiss.
 *
 * "Yes, I'm verified" re-triggers auto-join now that the email is verified. An invited learner just
 * needs a reload (org-home's own auto-join re-runs on mount). An allowlisted learner has no invitation
 * for that to act on, so we redirect to the program-scoped page instead — its own auto-join effect
 * (useProgramAutoJoinDomainAllowList) succeeds now and redirects back to org-home once it settles.
 */
const OrgScopedEmailVerifyModal = () => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const orgSlug = thirdPartyOrganization?.slug;
  const location = useLocation();
  const incomingProgramSlug = location.query.programSlug as string | undefined;
  // The org membership query doesn't fetch the invited email, so we use the learner's primary
  // account email — the unverified address for these learners.
  const email = getUser()?.email_address ?? '';

  const { resendVerificationEmail } = useResendVerificationEmail();
  const isExtraSmall = useMediaQuery(() => breakpoints.down('xs'));

  const [open, setOpen] = React.useState(true);
  const [isResending, setIsResending] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  const onResendEmail = async () => {
    if (!orgSlug) {
      return;
    }
    setIsResending(true);
    setHasError(false);
    try {
      await resendVerificationEmail({ orgSlug, email });
    } catch {
      setHasError(true);
    } finally {
      setIsResending(false);
    }
  };

  const onCheckVerified = () => {
    if (incomingProgramSlug) {
      // Landing back on the program-scoped page re-runs its useProgramAutoJoinDomainAllowList
      // effect, which now succeeds with the verified email and redirects to org-home once settled.
      redirectClientOrServer(`/programs/${incomingProgramSlug}`);
      return;
    }
    // Reload so org-home's own auto-join (invited-learner path) re-runs with the now-verified email.
    refresh();
  };

  const onClose = () => setOpen(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-describedby="org-email-verify-dialog-content"
      color="darkBlue"
      variant="standard"
    >
      <HeadingGroup>{_t('Verify your email address')}</HeadingGroup>
      <Content id="org-email-verify-dialog-content">
        <Typography2 component="p" variant="subtitleLarge" css={styles.subtitle}>
          {_t('Verify email to join Coursera.')}
        </Typography2>
        <Typography2 component="p">
          <FormattedMessage
            message={_t(
              'A verification email has been sent to {emailAddress}. After verification, please refresh or revisit this link to join Coursera.'
            )}
            emailAddress={<strong>{email}</strong>}
          />
        </Typography2>
        {hasError && (
          <InlineNotification severity="error" role="alert" css={styles.error}>
            {_t('An error occurred, please try again later.')}
          </InlineNotification>
        )}
      </Content>
      <Actions>
        <ButtonGroup
          aria-label={_t('Verification actions')}
          primaryButtonProps={{
            children: _t('Resend verification email'),
            onClick: onResendEmail,
            loading: isResending,
            fullWidth: isExtraSmall,
          }}
          alternateButtonProps={{
            children: _t("Yes, I'm verified"),
            onClick: onCheckVerified,
            variant: 'secondary',
            disabled: isResending,
            fullWidth: isExtraSmall,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default OrgScopedEmailVerifyModal;
